import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/components/AlarmProfiles/ProfileCard.jsx
import React from 'react';
import {
    Card,
    CardContent,
    CardActions,
    Button,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Divider
} from '@mui/material';
import {
    Edit as EditIcon,
    Assignment as AssignIcon,
    ContentCopy as DuplicateIcon,
    MoreVert as MoreIcon,
    Visibility as VisibilityIcon,
    Star as StarIcon
} from '@mui/icons-material';

const ProfileCard = ({
    profile,
    onEdit,
    onAssign,
    onDuplicate,
    onMenuOpen,
    onViewDetail,
    hasPermission,
    getProfileAssignments
}) => {
  const { t } = useTranslation();
    const profileAssignments = getProfileAssignments(profile.id);

    // Obtener configuraciones agrupadas por eje
    const getConfigsByAxle = () => {
        const configsByAxle = {};

        if (profile.applies_to_all_axles) {
            // Todos - mostrar una sola fila
            const psiConfig = profile.configurations?.find(c => c.metric_type === 'PSI' && c.axle_number === 1);
            const tempConfig = profile.configurations?.find(c => c.metric_type === 'TEMP' && c.axle_number === 1);

            configsByAxle['Todos'] = {
                psi: psiConfig,
                temp: tempConfig
            };
        } else {
            // Por eje - mostrar una fila por eje
            for (let i = 1; i <= profile.axle_count; i++) {
                const psiConfig = profile.configurations?.find(c => c.metric_type === 'PSI' && c.axle_number === i);
                const tempConfig = profile.configurations?.find(c => c.metric_type === 'TEMP' && c.axle_number === i);

                configsByAxle[t('hardcoded.components_alarmprofiles_profilecard.eje_i', { i: i })] = {
                    psi: psiConfig,
                    temp: tempConfig
                };
            }
        }

        return configsByAxle;
    };

    const formatThreshold = (config) => {
        if (!config) return '—';

        const min = config.threshold_min;
        const max = config.threshold_max;
        const ideal = config.ideal_value;

        let range = '';
        if (min != null && max != null) range = `${min}–${max}`;
        else if (min != null) range = `≥${min}`;
        else if (max != null) range = `≤${max}`;
        else range = '—';

        if (ideal != null) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {range}
                    <Tooltip title={t('hardcoded.components_alarmprofiles_profilecard.ideal_ideal', { ideal: ideal })}>
                        <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                    </Tooltip>
                </Box>
            );
        }

        return range;
    };

    const configsByAxle = getConfigsByAxle();

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': { boxShadow: 2 },
                cursor: 'pointer'
            }}
            onClick={() => onViewDetail(profile)}
        >
            <CardContent sx={{ flex: 1, p: 1.5, pb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {profile.name}
                        </Typography>
                        {profile.description && (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {profile.description}
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {profile.is_template && (
                            <Chip label={t('hardcoded.components_alarmprofiles_profilecard.plantilla')} size="small" color="secondary" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        <Chip
                            label={profile.active ? t('common.active') : t('hardcoded.components_alarmprofiles_assignmentstable.inactivo')}
                            size="small"
                            color={profile.active ? 'success' : 'default'}
                            variant={profile.active ? 'filled' : 'outlined'}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                        />

                        {hasPermission('manager') && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMenuOpen(e, profile);
                                }}
                                sx={{ ml: 0.25 }}
                            >
                                <MoreIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {profile.axle_count} {i18n.t('hardcoded.components_alarmprofiles_assignmentstable.ejes')}{profile.applies_to_all_axles ? t('hardcoded.components_alarmprofiles_assignmentstable.todos') : t('hardcoded.components_alarmprofiles_profilecard.por_eje')}
                    </Typography>
                </Box>

                <Box sx={{ mt: 1 }}>
                    <Table size="small" sx={{ '& td, & th': { py: 0.25, fontSize: '0.72rem' } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                    {profile.applies_to_all_axles ? t('hardcoded.components_alarmprofiles_profilecard.aplicacion') : t('vehicleDetail.alarms.axle')}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                    {t('hardcoded.components_alarmprofiles_profilecard.presion')}</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: 'text.secondary' }}>
                                    {i18n.t('hardcoded.components_alarmprofiles_profilecard.temperatura')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.entries(configsByAxle).map(([axleName, configs]) => (
                                <TableRow key={axleName}>
                                    <TableCell>{axleName}</TableCell>
                                    <TableCell>
                                        {formatThreshold(configs.psi)}
                                        {configs.psi && ` ${configs.psi.unit}`}
                                    </TableCell>
                                    <TableCell>
                                        {formatThreshold(configs.temp)}
                                        {configs.temp && ` ${configs.temp.unit}`}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>

                <Box
                    sx={{
                        mt: 1.25,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        px: 1,
                        py: 0.75
                    }}
                >
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {i18n.t('gps.stats.assigned')}</Typography>
                        <Typography variant="body2" fontWeight={700}>
                            {profileAssignments.length}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>

            <CardActions sx={{ pt: 0.5, px: 1.5, pb: 1 }} onClick={(e) => e.stopPropagation()}>
                {profile.is_template ? (
                    <Button
                        size="small"
                        startIcon={<DuplicateIcon />}
                        onClick={() => onDuplicate(profile)}
                        disabled={!hasPermission('manager')}
                        fullWidth
                        variant="outlined"
                    >
                        {i18n.t('hardcoded.components_alarmprofiles_profilecard.usar')}</Button>
                ) : (
                    <Button
                        size="small"
                        startIcon={<AssignIcon />}
                        onClick={() => onAssign(profile)}
                        disabled={!hasPermission('manager') || !profile.active}
                        fullWidth
                        variant="contained"
                    >
                        {i18n.t('common.assign')}</Button>
                )}

                <Tooltip title={t('hardcoded.components_alarmprofiles_profilecard.ver_detalle')}>
                    <IconButton size="small" onClick={() => onViewDetail(profile)}>
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                {hasPermission('manager') && (
                    <Tooltip title={t('common.edit')}>
                        <IconButton size="small" onClick={() => onEdit(profile)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </CardActions>
        </Card>
    );
};

export default ProfileCard;